/**
 * Per-site feature flags.
 *
 * One codebase serves every domain: a deploy ships the same code to all of them
 * and only the SITE env var differs. So "try this on one site first" has to be a
 * flag in that site's sites/<domain>/site.config.js, the same mechanism that
 * already gates `features.admin`.
 *
 * Add a flag by giving it a default here, then set it in a single site's config:
 *
 *   // sites/tofjan.online/site.config.js
 *   export default {
 *     name: 'tofjan',
 *     features: { admin: true, cms: true, appearance: true },
 *   }
 *
 * Works in the browser too, because lib/server.js injects features into
 * window.__SITE_CONFIG__ for the admin bundle.
 */

import { site } from './site.js'

/**
 * Defaults. A boolean in site.config.js overrides the value here.
 * Anything not listed defaults to false.
 */
export const FEATURE_DEFAULTS = {
    // Long-standing flags already used by the router and API.
    admin: false,
    cms: false,

    // Content editor (theme drafts, live preview, publish). On for every site;
    // set false in site.config.js to keep the previous editor.
    appearance: true,

    // Blocks page canvas, on with the Content editor. Set false to opt out.
    pageBlocks: true,

    // Visitor-facing push notifications. Slice E, needs a consent UI.
    visitorPush: false,

    // The optional Unlayer landing-page block. Off by default: it stores its own
    // design JSON and edits pass through a hosted third-party iframe, so it is a
    // deliberate choice per site rather than something available everywhere.
    unlayerPages: false,

    // Password-protected downloads at /resources and its admin page. Retired;
    // the code stays so a site can switch it back on.
    resources: false,
}

/**
 * @param {string} name - Flag name
 * @returns {boolean}
 */
export function isFeatureEnabled(name) {
    const configured = site?.features?.[name]
    if (typeof configured === 'boolean') return configured
    return Boolean(FEATURE_DEFAULTS[name])
}

/**
 * The resolved flags, for injecting into the browser or debugging.
 * @returns {Object} name -> boolean
 */
export function resolveFeatures() {
    const names = new Set([
        ...Object.keys(FEATURE_DEFAULTS),
        ...Object.keys(site?.features || {}),
    ])

    return Object.fromEntries([...names].map((name) => [name, isFeatureEnabled(name)]))
}

export default { isFeatureEnabled, resolveFeatures, FEATURE_DEFAULTS }
